/**
 * Supported upload file formats for Document Ingest, and how each is turned
 * into text. Shared by the Studio upload control (the `accept` string) and the
 * server extractor (dispatch by kind), so the two never drift.
 */
import { DocumentType } from "./DocumentConcept.Data";

export type DocumentFileKind =
  | "pdf" // pdf-parse text layer; scanned → vision
  | "docx" // mammoth
  | "doc" // legacy binary Word — not text-extractable here
  | "xlsx" // exceljs
  | "csv" // decode as text
  | "txt" // decode as text
  | "image" // png/jpg/… — no text layer; vision
  | "unknown";

/** Map a filename's extension to a kind. */
export function fileKindFromName(fileName: string): DocumentFileKind {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  switch (ext) {
    case "pdf":
      return "pdf";
    case "docx":
      return "docx";
    case "doc":
      return "doc";
    case "xlsx":
    case "xlsm":
      return "xlsx";
    case "csv":
      return "csv";
    case "txt":
    case "md":
      return "txt";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
      return "image";
    default:
      return "unknown";
  }
}

/** Best-guess MIME for a filename (used to set the vision/document content-block media type). */
export function mediaTypeFromName(fileName: string): string {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    default:
      return "application/octet-stream";
  }
}

/** `accept` attribute for the upload <input type="file">. */
export const DOCUMENT_UPLOAD_ACCEPT = [".pdf", ".docx", ".doc", ".xlsx", ".csv", ".txt", ".png", ".jpg", ".jpeg", ".gif", ".webp"].join(",");

/** Lower-cased extension (no dot) of a filename; "" if none. */
export function extensionOf(fileName: string): string {
  const parts = fileName.split(".");
  return parts.length > 1 ? (parts.pop() || "").toLowerCase() : "";
}

/**
 * Whether a filename is an accepted upload format. Used at presign time to
 * reject unsupported files up front (and to gate the client dropzone), so the
 * `accept` list and this check never drift.
 */
export function isSupportedUpload(fileName: string): boolean {
  return fileKindFromName(fileName) !== "unknown";
}

/**
 * Auto-detect a document type from the filename plus an optional sample of the
 * extracted text. Used ONLY to pick the parsing/extraction path and for display
 * — it is not operator-chosen and does not gate the bucket-scan (which always
 * runs on every group). When the content signal is weak/ambiguous, this returns
 * the fuller `StoragePolicySheet` rather than a narrow type, so nothing is
 * scoped away by a wrong guess.
 */
export function detectDocTypeFromContent(fileName: string, textSample?: string): DocumentType {
  const kind = fileKindFromName(fileName);
  const text = (textSample || "").toLowerCase();

  if (/\b(employee|staff)\b/.test(text) && /\bhandbook\b/.test(text)) return DocumentType.EmployeeHandbook;
  if (/\b(call (quality|qa)|qa rubric|scorecard|call monitoring)\b/.test(text)) return DocumentType.CallQaRubric;
  if (/\b(lease agreement|rental agreement|this lease|lessor|lessee|tenant agrees)\b/.test(text)) return DocumentType.Lease;
  if (/\b(faq|frequently asked questions)\b/.test(text)) return DocumentType.Faq;

  // Spreadsheets / rate-card-like content lean fee-sheet — but only on a clear signal.
  const looksLikeFees = /\b(fee schedule|rate sheet|price list|monthly rate|admin fee|late fee)\b/.test(text);
  if ((kind === "xlsx" || kind === "csv") && looksLikeFees) return DocumentType.FeeSheet;
  if (looksLikeFees && !/\bpolic(y|ies)\b/.test(text)) return DocumentType.FeeSheet;

  // Default to the fuller policy-sheet type; ambiguity should not narrow scope.
  return DocumentType.StoragePolicySheet;
}
